import { ViewData } from '../../model/view.data';
import { MatrixDataLoad } from "../../vo/matrix/matrix.dataload";
import { AkmBDD, BddMatrix } from "@wizeoo/akm-worker-lib";
import { take } from "rxjs";
import { Matrix } from "@wizeoo/akm-message-lib";
import { MatrixCommand } from "./matrix.cmd";
import { Model } from '../../model/model';

import { AllkeyWebWorker } from '../../manager/worker/allkey.web.worker';
import { WizCommand, WizGroupCommand } from '@wizeoo/akm-component-lib';

/** Service de gestion des matrices de données */
export class MatrixSyncService{

  protected model: Model = null;

  constructor(model: Model) {
    this.model = model;
  }

  // Pack de chargement
  public dataload: MatrixDataLoad = null;

    /** la clé du pack, domain + BU */
    public get key(): string {
      return this.model.domainCode + ViewData.KEY_SEPARATOR + this.model.businessUnitCode;
    }
   
  public resetAll(): void {
    this.dataload = null;
    this.model.matrixEngine.reset();
  }

  public find(callback: Function, force: boolean = false): void {

    const uniqueKey: string = this.key;
    if (!!this.dataload) { // j'en ai déjà un, je contrôle sa clé (init multi BU...)   
      if (this.dataload.uniqueKey !== uniqueKey) {
        this.dataload = null; // j'en ai plus...
      }
    }
    
    if (!force) { // je suis pas en train de forcer
      if (this.dataload?.state === MatrixDataLoad.COMPLETE) { // et il est déjà complet
        callback(this.dataload.localMatrix); // je renvoie direct le pack
        return;
      }
    }

    if (!this.dataload || force) { // pas de pack, ou demande de forçage
      this.dataload = new MatrixDataLoad(this.model.domainCode, this.model.businessUnitCode);
      this.dataload.callbacks.push(callback); //  = callback;

      // chargement local
      AkmBDD.getInstance().doFindMatrix(uniqueKey)
        .pipe(take(1))
        .subscribe((bddMatrix: BddMatrix[]) => {
        
        let locaux: Map<string, Matrix> = new Map();
        let local: Matrix;
        let localRevisionNumber: number = -1;
  
        if(!!bddMatrix) {
          // console.log('fds', bddFDs);
          bddMatrix.forEach((bddmat: BddMatrix) => {
            local = bddmat.value;
            locaux.set(bddmat.key, local);
            if(local.revisionNumber > localRevisionNumber) {
              localRevisionNumber = local.revisionNumber;
            }
          });
        }

        if (localRevisionNumber >= 0) { // alimentation du numRev
          this.dataload.revisionNumber = localRevisionNumber;
        }
        this.dataload.localMatrix = locaux; // alimentation du jeu local dans le pack

        // console.log('fd service', this.dataload.revisionNumber);
        new MatrixCommand(this.model).findForDataload(this.dataload, this.backFromFind.bind(this)).execute();
         // .count(this.dataload, this.backFromCount.bind(this)).execute();
      });
    } else { // j'ai déjà un pack en cours, non COMPLETE... J'attends
      this.dataload.callbacks.push(callback); //  = callback;
    }
  }

  /**
   * retour de find, comparaison des versions / matrice
   * @param cmd 
   */
  private backFromFind(cmd: MatrixCommand): void {
    let dataLoad: MatrixDataLoad = cmd.dataload;
    let res: Matrix[] = cmd.result?.entities;
     
    //console.log('matrix', dataLoad, res);
  
    // map des objets back
    let remoteMap: Map<number, Matrix> = new Map(res?.map(item => [item.id, item])) || new Map();

    // map des objets locaux
    let localMap: Map<number, Matrix> = new Map();

    // map des matrices à charger
    let toLoadMatrix: Map<number, Matrix> = new Map();

    // const createSet: Map<number, Matrix> = new Map(); // les créations
    //const updatedSet: Map<number, Matrix> = new Map<number, Matrix>(); // les updates
    const toDieSet: Map<number, any> = new Map(); // je vois des gens qui sont morts..


    this.dataload.localMatrix.forEach((mat: Matrix) => { // on garde les pas morts
      if (!remoteMap.has(mat.id)) { // plus dans les vivants...
        toDieSet.set(mat.id, mat); 
      } else { // existe back, contrôle de version
        // on alimente la map des locaux pour le contrôle des create
        localMap.set(mat.id, remoteMap.get(mat.id));
        if(remoteMap.get(mat.id).version > mat.version) { // plus frais, on charge
          localMap.set(mat.id, remoteMap.get(mat.id)); // version plus fraiche en remote
          toLoadMatrix.set(mat.id, remoteMap.get(mat.id));
        }
      }
    });

    [...remoteMap.values()].forEach(mat => {
      if(!localMap.has(mat.id)) { // creation, pas encore dispo
        toLoadMatrix.set(mat.id, mat);
      }
    });

    this.dataload.localMatrix = new Map([...localMap.values()].map(item => [item.code, item]));
    
    if(toLoadMatrix.size > 0) { // besoins de chargements
      this.callDatas(dataLoad, toLoadMatrix, toDieSet);
    } else {
      // en cas de suppression
      this.dataload = dataLoad;
      this.dataload.state = MatrixDataLoad.COMPLETE; // ok, chargé
        
      if(toDieSet.size > 0) {
        this.dataload.toDie = toDieSet;
        this.writeBDD(this.dataload)
      } 
         
      this.resolveCallbacks();
    }
  }

  /** on remercie les callbacks, en leur passant le pool local (source) */
  private resolveCallbacks(): void {
    
    // alimentation des collections
    this.model.matrixEngine.feedMatrix([...this.dataload.localMatrix.values()]);
    
    this.dataload.callbacks.forEach((f: Function) => {
      f(this.dataload.localMatrix);
    })
    this.dataload.callbacks = []; // on rince les callbacks;
  }

  /**
   * appel remote pour charger les datas manquantes ou en besoin de refresh
   * @param dataload 
   * @param toLoadMatrix 
   * @param toDieSet 
   */
  private callDatas(dataload: MatrixDataLoad, toLoadMatrix: Map<number, Matrix>, toDieSet: Map<number, Matrix>): void {
      
      const commands: WizCommand<any>[] = []; 
      [...toLoadMatrix.values()].forEach(mat => {
        let cmd = new MatrixCommand(Model.getInstance()).getForDataload(mat);
        commands.push(cmd);
      })

      const grpCmd = new WizGroupCommand().configure(commands, this.backFromRemote.bind(this), true);
      grpCmd.carrier = [dataload, toLoadMatrix, toDieSet];
      grpCmd.execute();
      
  }

  /**
   * retour sur appel des GET pour les matrices
   * @param cmd 
   */
  private backFromRemote(cmd: WizGroupCommand): void {

      let dlo = cmd.carrier[0];
      let toLoadMatrix: Map<number, Matrix> = cmd.carrier[1];
      let toDieSet: Map<number, any> = cmd.carrier[2];
      // write sur MatrixData
      // console.log('backFromRemote', cmd);
      this.dataload = dlo;
      this.dataload.state = MatrixDataLoad.COMPLETE; // ok, chargé
      
      // les matrices sans ROWs d'un côté, le reste dans MatrixData
      // toPersist inutile ?
      this.dataload.toPersist = new Map([...toLoadMatrix.values()].map(item => ([item.code, item])));
      this.dataload.toDie = toDieSet;
      this.writeBDD(this.dataload);

      // les matrices complètes
      let newMats: Matrix[] = cmd.commandPile.map(wc => wc.result.entity);
      newMats.forEach(m => {
        this.dataload.localMatrix.set(m.code, m);
      });

      this.writeBDDDatas(newMats);

      this.resolveCallbacks();

  }


  /** persistence local par le serviceWorker */
  private writeBDD(dataload: MatrixDataLoad): void {
    AllkeyWebWorker.storeMatrix(dataload);
  }

  /** ajout des matrices (rows) */
  private writeBDDDatas(mats: Matrix[]): void {
    AllkeyWebWorker.storeMatrixDatas(mats);
  }

  /**
   * exécution de requêtes Dexie sur la table des matrices
   * @param query 
   * @returns 
   */
  // public async runQuery(query: string): Promise<any> {
  //   try {
  //     const fn = new Function('db', `
  //       return (async () => {
  //         return ${query};
  //       })();
  //     `);

  //     return await fn(AkmBDD.getInstance());

  //   } catch (e) {
  //     throw e;
  //   }
  // }



}